/**
 * Scroll gate for the teaser (and the section questions that reuse it): the
 * proactive text waits until the visitor has scrolled `screens` viewport
 * heights from the top of the document. Once reached it stays reached for the
 * page view. `screens` of 0 means "no gate": reached from the start.
 *
 * Reads are passive: one `scroll` listener, throttled to an animation frame,
 * reading `scrollY` and `innerHeight` only (no layout read). The environment
 * is injectable so the logic is testable without a browser.
 */

export type ScrollGateEnv = {
	/** Distance scrolled from the top of the document, in CSS pixels. */
	getScrollY: () => number;
	/** Viewport height in CSS pixels. */
	getViewportHeight: () => number;
	/** Run `callback` on the next frame; returns a cancel function. */
	requestFrame: ( callback: () => void ) => () => void;
	addScrollListener: ( listener: () => void ) => () => void;
};

export type ScrollGate = {
	isReached: () => boolean;
	destroy: () => void;
};

export function isScrollDepthReached(
	scrollY: number,
	viewportHeight: number,
	screens: number
): boolean {
	if ( ! ( screens > 0 ) ) {
		return true;
	}
	if ( ! ( viewportHeight > 0 ) || ! Number.isFinite( scrollY ) ) {
		return false;
	}
	return scrollY >= screens * viewportHeight;
}

export function defaultScrollGateEnv(): ScrollGateEnv {
	return {
		getScrollY: () =>
			window.scrollY ?? document.documentElement.scrollTop ?? 0,
		getViewportHeight: () => window.innerHeight,
		requestFrame: ( callback ) => {
			const id = window.requestAnimationFrame( callback );
			return () => window.cancelAnimationFrame( id );
		},
		addScrollListener: ( listener ) => {
			window.addEventListener( 'scroll', listener, { passive: true } );
			return () => window.removeEventListener( 'scroll', listener );
		},
	};
}

/**
 * @param {number}        screens   Viewport heights to scroll (0 = off).
 * @param {Function}      onReached Called once, when the depth is reached.
 * @param {ScrollGateEnv} env       Scroll/viewport readers.
 */
export function createScrollGate(
	screens: number,
	onReached: () => void,
	env: ScrollGateEnv = defaultScrollGateEnv()
): ScrollGate {
	let reached = false;
	let cancelFrame: ( () => void ) | null = null;
	let removeListener: ( () => void ) | null = null;

	const destroy = () => {
		cancelFrame?.();
		cancelFrame = null;
		removeListener?.();
		removeListener = null;
	};

	const check = () => {
		cancelFrame = null;
		if ( reached ) {
			return;
		}
		if (
			isScrollDepthReached(
				env.getScrollY(),
				env.getViewportHeight(),
				screens
			)
		) {
			reached = true;
			destroy();
			onReached();
		}
	};

	if ( ! ( screens > 0 ) ) {
		reached = true;
		onReached();
	} else {
		removeListener = env.addScrollListener( () => {
			if ( ! reached && ! cancelFrame ) {
				cancelFrame = env.requestFrame( check );
			}
		} );
		// The page may already be scrolled (restored position).
		check();
	}

	return { isReached: () => reached, destroy };
}
